import type { TeamStablefordPayload } from '@golf-app/shared';

import { teamStablefordQuota } from './teamStablefordQuota';

export interface TeamStablefordPlayerSummary {
  eventPlayerId: string;
  displayName: string;
  avatarUrl: string | null;
  playingHandicap: number;
  quota: number;
  grossPoints: number;
  netPoints: number;
  teamContribution: number;
  holesPlayed: number;
}

export interface TeamStablefordTeamSummary {
  team: TeamStablefordPayload['teams'][number];
  rank: number;
  teamTotal: number;
  grossPoints: number;
  uncappedNetPoints: number;
  holesPlayed: number;
  players: TeamStablefordPlayerSummary[];
}

export function buildTeamStablefordSummaries(
  payload: TeamStablefordPayload,
): TeamStablefordTeamSummary[] {
  const teamOrder = new Map(payload.teams.map((team, index) => [team.id, index]));
  const playerOrder = new Map(payload.participants.map((player, index) => [player.eventPlayerId, index]));
  const teamStandings = new Map(payload.teamStandings.map((standing) => [standing.teamId, standing]));
  const playerStandings = new Map(payload.playerStandings.map((standing) => [standing.eventPlayerId, standing]));

  const summaries = payload.teams.map((team) => {
    const standing = teamStandings.get(team.id);
    const players = payload.participants
      .filter((player) => player.teamId === team.id)
      .map((player) => {
        const playerStanding = playerStandings.get(player.eventPlayerId);
        return {
          eventPlayerId: player.eventPlayerId,
          displayName: player.displayName,
          avatarUrl: player.avatarUrl,
          playingHandicap: player.playingHandicap,
          quota: teamStablefordQuota(payload.config.points.par, payload.holes.length, player.playingHandicap),
          grossPoints: playerStanding?.points ?? 0,
          netPoints: playerStanding?.netPoints ?? 0,
          teamContribution: playerStanding?.teamContribution ?? 0,
          holesPlayed: playerStanding?.holesPlayed ?? 0,
        };
      })
      .sort((left, right) =>
        right.netPoints - left.netPoints
        || (playerOrder.get(left.eventPlayerId) ?? 0) - (playerOrder.get(right.eventPlayerId) ?? 0),
      );

    return {
      team,
      rank: 0,
      teamTotal: standing?.points ?? 0,
      grossPoints: standing?.grossPoints ?? 0,
      uncappedNetPoints: standing?.netPoints ?? 0,
      holesPlayed: standing?.holesPlayed ?? 0,
      players,
    };
  });

  summaries.sort((left, right) =>
    right.teamTotal - left.teamTotal
    || right.uncappedNetPoints - left.uncappedNetPoints
    || right.grossPoints - left.grossPoints
    || (teamOrder.get(left.team.id) ?? 0) - (teamOrder.get(right.team.id) ?? 0),
  );

  return summaries.map((summary, index) => ({ ...summary, rank: index + 1 }));
}
